import { supabase } from '@/integrations/supabase/client';

/**
 * "The customer took the vehicle before the work was finished."
 *
 * That is a CUSTOMER/PHYSICAL fact. It is not an assertion that Tech A paused,
 * that Tech B stopped, or that the newest timer session ended at this instant —
 * but until 8G.2C the product made exactly those assertions on the
 * technicians' behalf, and did it in two separate mutations with the physical
 * one committing first.
 *
 * The server now performs the departure and the lifecycle fact in ONE
 * transaction, refuses while any technician is working, paused or owed an
 * allocation review, and fabricates no pause.
 */
export type PickupOutcome =
  /** The vehicle is recorded as gone and the work as unfinished. */
  | 'PICKED_UP_INCOMPLETE'
  /** A repeat after a lost response. Not a second handover. */
  | 'ALREADY_PICKED_UP'
  | 'ACTIVE_WORK'
  | 'PAUSED_WORK'
  | 'REVIEW_REQUIRED'
  | 'TIMER_INTEGRITY_BLOCK'
  /** The space could not be released. */
  | 'LOCATION_CONFLICT'
  /** Unknown, another tenant's, or one this caller may not hand over. */
  | 'TICKET_NOT_AVAILABLE'
  /** No server answer. Does NOT mean nothing committed. */
  /**
   * 8G.2C.7: a departure stamp on a ticket that is not in the state this act
   * ends in — a legacy row from the retired generic pickup, most often. The
   * server refuses; a person reconciles.
   */
  | 'PICKUP_STATE_CONFLICT'
  /** Oct 20 (FD-06): a pickup reason is required. Nothing was written. */
  | 'REASON_REQUIRED'
  /** Oct 20 (FD-07): an expected return date cannot be in the past. Nothing was written. */
  | 'RETURN_DATE_IN_PAST'
  /** Oct 20 addendum (L2): the paused sessions changed since the person reviewed them. Nothing was written. */
  | 'PAUSED_WORK_CHANGED'
  /** Oct 20 addendum (L2): only a Service Advisor, Manager or Owner may end paused work at pickup. */
  | 'PAUSED_RELEASE_NOT_AUTHORIZED'
  /** Oct 20 addendum (L3): a later day of the repair already carries technician work. */
  | 'LATER_DAY_HAS_WORK'
  /** Oct 20 addendum (L3): the vehicle is recorded on another day of the repair. */
  | 'LOCATION_ON_OTHER_DAY'
  /** Oct 20 addendum: a machine is still running on the vehicle. */
  | 'PROTECTED_OPERATION_ACTIVE'
  /** Oct 20 addendum (L3): the days of the repair changed while the act started. Nothing was written. */
  | 'FAMILY_CHANGED_RETRY'
  /**
   * Oct 20 addendum (L2, isolated pending a constitutional decision): a paused diagnostic job still owes its outcome;
   * the technician records it or ends their own work. Nothing was written.
   */
  | 'DIAGNOSTIC_OUTCOME_OWED'
  | 'SUBMIT_FAILED';

/** Oct 20 addendum (L2): one paused technician session the departure would end (what the dialog lists). */
export interface DeparturePausedSession {
  pauseSessionId: string;
  ticketId: string;
  technicianId: string | null;
  technicianName: string | null;
  pausedAt: string;
  pauseReason: string;
  measuredMinutes: number;
  /** Diagnostic jobs whose outcome is still owed. The release never records one. */
  diagnosticOutcomesOwed: Array<{ job_id: string; name: string | null }>;
}

export function parsePausedSessions(raw: unknown): DeparturePausedSession[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((p: any) => ({
    pauseSessionId: String(p.pause_session_id),
    ticketId: String(p.ticket_id),
    technicianId: p.technician_id ?? null,
    technicianName: p.technician_name ?? null,
    pausedAt: String(p.paused_at),
    pauseReason: String(p.pause_reason ?? ''),
    measuredMinutes: Number(p.measured_minutes ?? 0),
    diagnosticOutcomesOwed: Array.isArray(p.diagnostic_outcomes_owed) ? p.diagnostic_outcomes_owed : [],
  }));
}

export interface PickupResult {
  outcome: PickupOutcome;
  ticketId: string;
  activeSessions: number;
  unresolvedPauses: number;
  awaitingReview: number;
  note: string | null;
  /**
   * The ticket's CANONICAL shop, resolved and authorized server-side under the
   * ticket transition lock. Present only on settled outcomes.
   *
   * Durable side effects — reminders, owner escalation, cascade, audit — must
   * attribute to this, not to whatever shop the browser's React context
   * happened to hold when the button was pressed.
   */
  shopId: string | null;
  /** Final status. A settled incomplete pickup is always 'Blocked'. */
  status: string | null;
  /**
   * 8G.2C.9 §6: the CANONICAL departure instant, from the server, on both
   * PICKED_UP_INCOMPLETE and ALREADY_PICKED_UP.
   *
   * The RPC always returned this and the typed result discarded it, so every
   * derived schedule was anchored to whenever the browser happened to run —
   * which on a recovery retry is not when the customer left.
   */
  pickedUpAt: string | null;
  /** Oct 20 addendum (L2): on PAUSED_WORK / PAUSED_WORK_CHANGED, the sessions to show and confirm. */
  pausedSessions?: DeparturePausedSession[];
  /** Oct 20 addendum (L3): future days withdrawn and the lanes they held (for the board re-walk). */
  withdrawnFutureDays?: Array<{ ticket_id: string; scheduled_date: string | null }>;
  lanes?: Array<{ technician_id: string; scheduled_date: string }>;
  releasedPausedWork?: number;
}

const KNOWN: PickupOutcome[] = [
  'PICKUP_STATE_CONFLICT',
  'PICKED_UP_INCOMPLETE', 'ALREADY_PICKED_UP', 'ACTIVE_WORK', 'PAUSED_WORK',
  'REVIEW_REQUIRED', 'TIMER_INTEGRITY_BLOCK', 'LOCATION_CONFLICT',
  'TICKET_NOT_AVAILABLE', 'REASON_REQUIRED', 'RETURN_DATE_IN_PAST',
  'PAUSED_WORK_CHANGED', 'PAUSED_RELEASE_NOT_AUTHORIZED', 'LATER_DAY_HAS_WORK', 'LOCATION_ON_OTHER_DAY',
  'PROTECTED_OPERATION_ACTIVE', 'FAMILY_CHANGED_RETRY', 'DIAGNOSTIC_OUTCOME_OWED',
];

export async function markPickedUpIncomplete(
  ticketId: string, reason?: string | null,
  /**
   * Oct 20 (FD-07): optional, informational expected return date (YYYY-MM-DD). Never an appointment: the server
   * reserves no capacity, assigns no technician and creates no day from it.
   */
  expectedReturnDate?: string | null,
  /**
   * Oct 20 addendum (L2): the paused sessions the person reviewed and confirmed ending with the departure. Sent only
   * when given; the server ends exactly these (refusing PAUSED_WORK_CHANGED if the set changed) and never a running one.
   */
  releasePauseIds?: readonly string[] | null,
): Promise<PickupResult> {
  const args: Record<string, unknown> = {
    p_ticket_id: ticketId, p_reason: reason ?? null, p_expected_return_date: expectedReturnDate ?? null,
  };
  if (releasePauseIds && releasePauseIds.length > 0) args.p_release_pause_ids = [...releasePauseIds];
  const { data, error } = await (supabase as any).rpc('mark_picked_up_incomplete_if_quiescent', args);
  if (error) {
    console.warn('[markPickedUpIncomplete] failed', error);
    // 8G.2C.8: `status: null` was MISSING here and `npm run typecheck`
    // reported it (TS2741). Not cosmetic: `status` is what a settled
    // incomplete pickup is validated on, so the error branch must state
    // plainly that it knows nothing about the status rather than omit it.
    return { outcome: 'SUBMIT_FAILED', ticketId, status: null, pickedUpAt: null,
             activeSessions: 0,
             unresolvedPauses: 0, awaitingReview: 0, note: error.message,
             shopId: null };
  }
  const r = (data ?? {}) as Record<string, unknown>;
  // Unknown outcomes FAIL CLOSED: the vocabulary belongs to the server, and a
  // client too old to understand a new blocker must not report a handover.
  let outcome = KNOWN.includes(r.outcome as PickupOutcome)
    ? (r.outcome as PickupOutcome)
    : 'SUBMIT_FAILED';

  const shopId = (r.shop_id as string) ?? null;
  const status = (r.status as string) ?? null;
  const pickedUpAt = (r.picked_up_at as string) ?? null;

  // 8G.2C.7 — A SETTLED RESULT MUST CARRY THIS ACT'S OWN END STATE.
  //
  // The mirror of the completed-pickup rule. `picked_up_at` is a PHYSICAL
  // marker, so a settled answer is only this act's if the ticket is in the
  // state this act produces — `status='Blocked'`. Measured before the server
  // fix, a legacy Active row carrying a stamp returned
  // `{ ALREADY_PICKED_UP, status: 'Active' }`, and a client keyed on the
  // outcome string alone would have reported that as a successful recovery and
  // cancelled the customer's reminders for it.
  //
  // The server no longer produces that shape. This is the second line: a
  // settled incomplete pickup is Blocked, by definition, and anything else
  // fails closed to a reload.
  // 8G.2C.9 §6: `pickedUpAt` joins the settled requirements. Without it the
  // derived schedules below have nothing to anchor to, and substituting the
  // browser clock is exactly the defect §7 removes.
  if ((outcome === 'PICKED_UP_INCOMPLETE' || outcome === 'ALREADY_PICKED_UP')
      && !(shopId && status === 'Blocked' && pickedUpAt)) {
    console.warn('[markPickedUpIncomplete] settled without canonical end state', {
      ticketId, shopId, status, pickedUpAt,
    });
    outcome = 'SUBMIT_FAILED';
  }

  return {
    outcome,
    ticketId: (r.ticket_id as string) ?? ticketId,
    status,
    pickedUpAt,
    activeSessions: Number(r.active_sessions ?? 0),
    unresolvedPauses: Number(r.unresolved_pauses ?? 0),
    awaitingReview: Number(r.awaiting_review ?? 0),
    note: (r.note as string) ?? (r.reason as string) ?? null,
    shopId,
    pausedSessions: parsePausedSessions(r.paused_sessions),
    withdrawnFutureDays: Array.isArray(r.withdrawn_future_days) ? (r.withdrawn_future_days as any[]) : [],
    lanes: Array.isArray(r.lanes) ? (r.lanes as any[]) : [],
    releasedPausedWork: Array.isArray(r.released_paused_work) ? (r.released_paused_work as unknown[]).length : 0,
  };
}

/** May the UI move on? */
export const isPickupSettled = (o: PickupOutcome) =>
  o === 'PICKED_UP_INCOMPLETE' || o === 'ALREADY_PICKED_UP';

/**
 * The server may or may not have committed. Reload; do not replay.
 *
 * 8G.2C.8: the same minimum law Restore (8G.2C.4 §17) and Completed Pickup
 * (8G.2C.5 §10) already follow. It was missing here, and the copy actively
 * told the operator to retry a real-world act.
 */
export const needsPickupReload = (o: PickupOutcome) => o === 'SUBMIT_FAILED';

/**
 * THIS handover just happened, in response to THIS click.
 *
 * 8G.2C.10 §13: this export carried TWO consecutive documentation blocks, and
 * the older one still attributed the derived consequences and the human-act
 * record to this flag. Neither is true, and the correction runs in both
 * directions:
 *
 *   - the DERIVED consequences moved out in 8G.2C.8, because gating them on
 *     the first act meant a lost response left the customer's reminders live
 *     forever;
 *   - the HUMAN-ACT record moved to the SERVER in 8G.2C.9, because gating it
 *     here gave at-most-once — a commit whose reply was lost produced no
 *     record at all.
 *
 * What is left is narrow: whether this click is the one that performed the act,
 * used for wording and for client-side effects that must not repeat. It is not
 * the authority for either category above.
 */
export const didPickUp = (o: PickupOutcome) => o === 'PICKED_UP_INCOMPLETE';

export function describePickupRefusal(r: PickupResult): string {
  switch (r.outcome) {
    case 'ACTIVE_WORK':
      return (r.activeSessions === 1
        ? 'A technician is still working on this vehicle.'
        : `${r.activeSessions} technicians are still working on this vehicle.`)
        + ' Running work is never ended from the front office — ask them to pause or stop, then record the pickup.';
    case 'PAUSED_WORK':
      return r.unresolvedPauses === 1
        ? 'A technician has paused work here. Review it and confirm ending it with the pickup.'
        : `${r.unresolvedPauses} technicians have paused work here. Review it and confirm ending it with the pickup.`;
    case 'PAUSED_WORK_CHANGED':
      return 'The paused work on this vehicle changed since you looked. Review it again — nothing was recorded.';
    case 'PAUSED_RELEASE_NOT_AUTHORIZED':
      return 'Only a Service Advisor, Manager or Owner can end a technician’s paused work at pickup.';
    case 'LATER_DAY_HAS_WORK':
      return 'A later day of this repair already has technician work. Record the pickup from that day.';
    case 'LOCATION_ON_OTHER_DAY':
      return 'The vehicle is recorded on another day of this repair. Record the pickup from that day.';
    case 'PROTECTED_OPERATION_ACTIVE':
      return 'A machine is still running on this vehicle. End it before the vehicle leaves.';
    case 'DIAGNOSTIC_OUTCOME_OWED':
      return 'A paused diagnostic job still needs its outcome from the technician. They record it (Partial or '
        + 'Not reproduced are outcomes) or end their own work; then record the pickup.';
    case 'FAMILY_CHANGED_RETRY':
      return 'The days of this repair changed while the pickup was starting. Nothing was recorded — try again.';
    case 'REVIEW_REQUIRED':
      return 'Measured time on this ticket still needs allocating to jobs.';
    case 'TIMER_INTEGRITY_BLOCK':
      return 'This ticket has timer evidence that needs a person to look at it.';
    case 'LOCATION_CONFLICT':
      return r.note ?? 'The vehicle’s space could not be released.';
    case 'PICKUP_STATE_CONFLICT':
      // 8G.2C.8: this outcome had NO case, so it fell to `default: return ''`
      // and the kebab handler rendered a toast with a title and a blank body —
      // the operator was told something went wrong and nothing else.
      //
      // The copy deliberately does NOT say the earlier departure was recorded
      // by the canonical authority. Durable state cannot prove which writer
      // stamped the row (8G.2C.7 §5), so it describes what is observable: a
      // departure record exists, and the ticket is not in either pickup act's
      // end state.
      return 'This vehicle already has a departure record, but the ticket is not '
        + 'in the state a completed pickup or a picked-up-incomplete visit ends in. '
        + 'Reload and have someone reconcile the ticket before recording another '
        + 'pickup.';
    case 'TICKET_NOT_AVAILABLE':
      return r.note ?? 'This ticket is not available.';
    case 'REASON_REQUIRED':
      return 'Say why the customer is taking the vehicle before the work is finished.';
    case 'RETURN_DATE_IN_PAST':
      return 'The expected return date cannot be in the past. Leave it empty if it is not known.';
    case 'SUBMIT_FAILED':
      // 8G.2C.8: this used to say "Try again", contradicting the type's own
      // comment that SUBMIT_FAILED "does NOT mean nothing committed". A
      // handover is a real-world act with one referent — the customer either
      // drove away or did not — so an unknown transport outcome must not
      // instruct a blind replay.
      return 'The result could not be confirmed, and the pickup may have been '
        + 'recorded. Reload and check the ticket before recording it again.';
    default:
      return '';
  }
}
